import { Component, OnInit } from '@angular/core';
import { environment } from '@environments/environment';
import { finalize, take } from 'rxjs/operators';
import { PagesService } from '@services/pages.service';
import { LoaderService } from '@services/app/loader.service';
import { MetaService } from '@services/app/meta.service';
import { LdJsonService } from '@services/app/ld-json.service';
import { AppService } from '@services/app/app.service';
import { AppConfiguration } from '@schemas/app.interface';
import { UniversalService } from '@services/universal.service';
import { ProductService } from '@services/product.service';
import { UserService } from '@services/user.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { UserData } from '@schemas/user.interface';
import { ApiResponseModel } from '@shared/models/api-response.model';
import { HomepageSection } from '@shared/models/homepage-section.interface';
import { MemberBioService } from '@services/community/member-bio.service';
import { MemberBio } from '@schemas/community/member-bio';
import { TheArchiveService } from '@services/the-archive.service';
import { Article } from '@schemas/article';
import { SearchRequest } from '@schemas/search/search-request';
import { SearchService } from '@services/search/search.service';
import DateUtils from '@shared/utils/date-utils';
import { ForSale } from '@schemas/search/for-sale-response';
import { SearchOperatorsEnum } from '@shared/enums/search-api.enum';
import { Hit, SearchResponse } from 'src/app/modules/catalog/components/search/search-response.interface';
import { AuthService } from '@services/auth.service';
import { threadId } from 'worker_threads';

@Component({
  selector: 'app-home-view',
  templateUrl: './home-view.component.html',
  styleUrls: ['./home-view.component.scss'],
})
export class HomeViewComponent implements OnInit {
  // route params:
  data: HomePageData = null;
  env = environment;
  userSnapshot$: BehaviorSubject<UserData | null>;
  appConfig: AppConfiguration;
  // campaigns section
  campaignsShowcaseData: { items: HomepageSection[] } = { items: [] };

  // ** Disable we love **
  // we-love section
  // weLoveShowcaseData: {
  //   items: Hit[];
  //   saleBadge: boolean;
  //   currencyCode: string;
  // } = {
  //   items: [],
  //   saleBadge: false,
  //   currencyCode: 'EUR',
  // };
  recommendShowcaseData: {
    items: Hit[];
    saleBadge: boolean;
    currencyCode: string;
  } = {
    items: [],
    saleBadge: false,
    currencyCode: 'EUR',
  };
  users: Array<MemberBio> = new Array<MemberBio>();
  communityMembersSection: Array<HomepageSection> = new Array<HomepageSection>();
  theArchiveSection: Array<HomepageSection> = new Array<HomepageSection>();
  numOfProducts = <any>'';
  public config$: BehaviorSubject<AppConfiguration | null>;
  articles: Article[];
  searchRequest: SearchRequest;
  searchRequestUser: SearchRequest;
  itemsForSale: Array<ForSale> = new Array<ForSale>();

  constructor(
    private loaderService: LoaderService,
    private ldJsonService: LdJsonService,
    private metaService: MetaService,
    private pagesService: PagesService,
    private appService: AppService,
    private universalService: UniversalService,
    private productService: ProductService,
    private userService: UserService,
    private memberBioService: MemberBioService,
    private archiveService: TheArchiveService,
    private searchService: SearchService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.userSnapshot$ = this.userService.getUserData();
    this.config$ = this.appService.getAppConfigurationBehavior();
    this.appService
      .getAppConfigurationBehavior()
      .subscribe((newValue: AppConfiguration) => (this.appConfig = newValue));

    this.appService
      .getAppConfigurationObservable()
      .pipe(take(1))
      .subscribe((appConfig: AppConfiguration | null) => {
        if (appConfig != null) {
          this.metaService.getStaticPageMeta('home');
          this.ldJsonService.getForHome();
        }
      });
    this.pagesService
      .getHome()
      .pipe(
        finalize(() => {
          this.loaderService.triggerLoading.emit(false);
        })
      )
      .subscribe({
        next: (pageData: HomePageData) => {
          this.data = pageData;

          if (this.universalService.isBrowser) {
            const userAgentString = navigator.userAgent;
            let safariAgent = userAgentString.indexOf('Safari') > -1;
            const chromeAgent = userAgentString.indexOf('Chrome') > -1;

            // Discard Safari since it also matches Chrome
            if (chromeAgent && safariAgent) safariAgent = false;

            if (safariAgent) {
              if (this.data.slider.image_url_jpg && this.data.slider.extension_jpg) {
                this.data.slider.image_url = this.data.slider.image_url_jpg;
                this.data.slider.extension = this.data.slider.extension_jpg;
              }

              this.data.home.map(data => {
                if (data.image_url_jpg && data.extension_jpg) {
                  data.image_url = data.image_url_jpg;
                  data.extension = data.extension_jpg;
                }

                return data;
              });
            }
          }
        },
        error: () => {
          this.loaderService.triggerLoading.emit(false);
        },
      });

    // WE LOVE section
    this.getSaleBadgeState();
    // this.loadWeLoveProducts(); // ** Disable we love **
    this.loadRecommendProducts();
    this.loadCampaigns();
    this.loadCommunityMembers();
    this.loadTheArchive();
    this.getApprovedProducts();
  }

  loadCommunityMembers() {
    this.pagesService
      .getHomePageSections('community_member', 'en')
      .subscribe((response: ApiResponseModel<HomepageSection[]>) => {
        if (response.data) {
          this.communityMembersSection = response.data;
          const users_id = this.communityMembersSection.map(x => x.title).join(',');

          this.searchRequestUser = this.getSearchRequestUser(users_id);

          this.memberBioService.getUsers(users_id).subscribe(users => {
            this.searchService.getItemsForSale(this.searchRequestUser).subscribe(data => {
              this.itemsForSale = data;
              this.getUsersInfo(users);
            });
          });
        }
      });
  }

  loadTheArchive() {
    this.pagesService
      .getHomePageSections('the_archive', 'en')
      .subscribe((response: ApiResponseModel<HomepageSection[]>) => {
        if (response.data) {
          this.theArchiveSection = response.data;
          const articles_id = this.theArchiveSection.map(x => x.title).join(',');

          this.archiveService.getSelectedArticles(articles_id).subscribe(articles => {
            this.theArchiveSection.forEach(x => {
              articles.find(e => e.id?.toString() === x.title).sort = x.sort_order;
            });
            articles.sort(function (a, b) {
              return a.sort - b.sort;
            });
            this.articles = articles;
          });
        }
      });
  }

  getUsersInfo(users: MemberBio[]) {
    users.forEach(user => {
      user.listedItems = this.itemsForSale.find(x => x.userId === user.id.toString())?.count ?? 0;

      this.productService.getSellerSoldItems(user.id).subscribe(data => {
        user.totalItemsSold = data;
        const section = this.communityMembersSection.find(x => x.title === user.id.toString());

        user.cover_image = `${this.env.UPLOADS_IMGIX_URL}dynamic/homepage/${section?.image_url}.${section?.extension}`;
        user.sort = section.sort_order;
        this.users.push(user);
      });
    });
  }

  loadCampaigns() {
    this.pagesService
      .getHomePageSections('campaign', this.appConfig?.languageShortName)
      .subscribe((response: ApiResponseModel<HomepageSection[]>) => {
        this.campaignsShowcaseData.items = response.data;
      });
  }

  getWishlistProductIDsByUserOb$(): Observable<SearchResponse> {
    const searchRequest = this.getWeLoveSearchRequest(10);

    return this.searchService.searchProduct(searchRequest);
  }

  // ** Disable we love **
  // loadWeLoveProducts(): void {
  //   this.getWishlistProductIDsByUserOb$().subscribe((response: SearchResponse) => {
  //     this.weLoveShowcaseData.items = response?.hits;
  //     this.weLoveShowcaseData.currencyCode = this.appConfig?.currencyCode;
  //   });
  // }

  loadRecommendProducts() {
    this.userService.getUserData().subscribe(user => {
      if (user) {
        this.searchService
          .searchProduct(this.getRecommendSearchRequest(user.id))
          .subscribe((response: SearchResponse) => {
            this.recommendShowcaseData.items = response?.hits;
            this.recommendShowcaseData.currencyCode = this.appConfig?.currencyCode;
          });
      }
    });
  }

  private getSaleBadgeState(): void {
    this.appService.getAppSetting('sale-badge').subscribe((data: any) => {
      if (data.message === 'success') {
        // this.weLoveShowcaseData.saleBadge = data.model.status == 'active'; // ** Disable we love **
        this.recommendShowcaseData.saleBadge = data.model.status == 'active';
      }
    });
  }

  getApprovedProducts(): void {
    const { from, to } = getNewInTimestampsInSeconds(0, 24);

    this.searchRequest = this.getApproveProductsSearchRequest(from.toString(), to.toString());
    this.searchService.getAmount(this.searchRequest).subscribe(total => {
      this.numOfProducts = total > 0 ? new Intl.NumberFormat('de-DE').format(total) : 0;
    });
  }

  getApproveProductsSearchRequest(fromTimeStampSecond: string, toTimeStampSecond: string): SearchRequest {
    return {
      page: 0,
      pageSize: 1,
      filters: [
        {
          field: 'from',
          value: fromTimeStampSecond,
          operator: 'gte',
        },
        {
          field: 'to',
          value: toTimeStampSecond,
          operator: 'lte',
        },
      ],
      sorting: null,
      currency: '',
      language: '',
    };
  }

  getWeLoveSearchRequest(pageSize = 10): SearchRequest {
    return {
      page: 0,
      pageSize: pageSize,
      filters: [
        {
          field: 'discover',
          value: 'we-love',
          operator: SearchOperatorsEnum.EQUALS,
        },
      ],
      sorting: [
        {
          field: 'approved_at',
          order: 'desc',
        },
      ],
      currency: this.appConfig?.currencyCode,
      language: this.appConfig?.languageShortName,
      balanceCategories: true,
    };
  }

  getSearchRequestUser(users_id: string): SearchRequest {
    return {
      page: 0,
      pageSize: 0,
      filters: [
        {
          field: 'commission_user_id',
          value: users_id,
          operator: 'in',
        },
      ],
      sorting: null,
    };
  }

  getRecommendSearchRequest(user_id: number, pageSize = 10): SearchRequest {
    return {
      page: 0,
      pageSize: pageSize,
      filters: this.getUserFilter(user_id),
      sorting: [
        {
          field: 'recommend',
          order: 'desc',
        },
      ],
      currency: this.appConfig?.currencyCode,
      language: this.appConfig?.languageShortName,
    };
  }

  getUserFilter(user_id: number): import('@schemas/search/search-request').Filters[] {
    if (user_id) return [{ field: 'user_id', value: user_id?.toString(), operator: SearchOperatorsEnum.NUMERIC_EQUAL }];

    return null;
  }
}

export interface HomePageData {
  slider: Slider;
  home: Slider[];
}

export interface Slider {
  link: string;
  title: string;
  description: string;
  image_url: null | string;
  extension: string | 'jpg' | 'png' | 'heic';
  button: string;
  image_url_jpg: null | string;
  extension_jpg: string | 'jpg' | 'png' | 'heic';
}

export interface NewArrivalTimestampsInSeconds {
  from: number;
  to: number;
}

export function getNewInTimestampsInSeconds(to: number = 24, from: number = 24): NewArrivalTimestampsInSeconds {
  const toTimestamp = DateUtils.CurrentDayInMilliseconds - DateUtils.HoursToMilliseconds(to);
  const fromTimestamp = toTimestamp - DateUtils.HoursToMilliseconds(from);
  const fromTimestampInSeconds = DateUtils.MillisecondsToSeconds(fromTimestamp);
  const toTimestampInSeconds = DateUtils.MillisecondsToSeconds(toTimestamp);

  return { from: fromTimestampInSeconds, to: toTimestampInSeconds };
}
